import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sortBy } from 'lodash';
import ClientsDropdown from '@/components/new-client-dropdown';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { getRandomColor, isValidEmail } from '@/helpers';
import { useAddMember } from '@/hooks/api/team';
import {
  ApplicationContainer,
  ClientContainer,
  TeamContainer,
  UserContainer,
} from '@/providers';
import { useToast } from '@/hooks/use-toast';
import MultiEmailInput from '../multi-email-input';

const NewMemberModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('team');
  const { toast } = useToast();

  const { state: clientState } = ClientContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();

  const { state: teamState } = TeamContainer.useContainer();

  const defaultRole = useMemo(() => 'admin', []);
  const [role, setRole] = useState(defaultRole);
  const [clientIds, setClientIds] = useState([]);
  const [members, setMembers] = useState([]);
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState('');

  const addMember = useAddMember();

  const allClientIds = useMemo(() => clientState.clients.map(({ id }) => id), [
    clientState.clients,
  ]);

  const toolTipText = useMemo(
    () => (
      <>
        {t('invite_member_modal_role_tooltip_text')}
        <a
          href="https://support.sparrowcharts.com/"
          rel="noreferrer"
          target="_blank"
          className="text-blue-300 underline ml-1"
        >
          {t('invite_member_modal_role_tooltip_link')}
        </a>
      </>
    ),
    [t]
  );

  const resetState = useCallback(() => {
    setRole(defaultRole);
    setClientIds([]);
    setMembers([]);
    setEmailError('');
    setValidationError('');
    setIsLoading(false);
  }, [defaultRole]);

  useEffect(() => {
    if (role !== 'member') {
      setClientIds(allClientIds);
    } else {
      setClientIds([]);
    }
  }, [allClientIds, role]);

  useEffect(() => {
    if (members.length > 0) {
      setMembers(prev =>
        prev.map(member => ({
          ...member,
          role,
          clientIds: clientIds.map(id => id.toString()),
        }))
      );
    }
  }, [role, clientIds]);

  const closeModal = () => {
    resetState();
    onClose();
  };

  const validate = email => {
    const validEmail = isValidEmail(email);
    const isAlreadyAdded = members.some(member => member.email === email);
    const isAlreadyInTeam = teamState.members.some(
      member => member.user.email === email
    );

    if (!validEmail) {
      setEmailError(t('invite_member_modal_validation_errors_invalid_email'));
      return false;
    }

    if (isAlreadyAdded) {
      setEmailError(
        t('invite_member_modal_validation_errors.email_already_added')
      );
      return false;
    }

    if (isAlreadyInTeam) {
      setEmailError(t('invite_member_modal_validation_errors_email_in_team'));
      return false;
    }
    return true;
  };

  const validateForm = () => {
    if (members.length === 0) {
      setValidationError(
        t('invite_member_modal_validation_errors_no_team_member')
      );
      return false;
    }

    if (!role) {
      setValidationError(
        t('invite_member_modal_validation_errors_no_role_selected')
      );
      return false;
    }

    if (clientIds.length === 0) {
      setValidationError(
        t('invite_member_modal_validation_errors_no_workspaces_selected')
      );
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleAddClick = email => {
    const isValid = validate(email);

    if (!isValid) {
      return;
    }

    setMembers(prev => [
      ...prev,
      {
        email: email.toLowerCase(),
        role,
        clientIds: clientIds.map(id => id.toString()),
        color: getRandomColor(),
      },
    ]);
  };

  const handleRemoveClick = removeEmail => {
    setMembers(prev => prev.filter(member => member.email !== removeEmail));
  };

  const handleApplyClick = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await Promise.all(members.map(member => addMember(member)));

      toast({
        variant: 'success',
        title: t('invite_member_modal_notifications_success', {
          count: members.length,
        }),
      });

      setMembers([]);
      closeModal();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t(
          'invite_member_modal_notifications_error',
          `An error occurred: ${error?.message}`,
          {
            message: error?.message,
          }
        ),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClientsChange = ids => {
    setClientIds(ids);
  };

  const roleOptions = sortBy(
    [
      { label: t('invite_member_modal_roles_member'), value: 'member' },
      { label: t('invite_member_modal_roles_admin'), value: 'admin' },
    ],
    'label'
  );

  return (
    <Dialog open={isOpen} onOpenChange={closeModal}>
      <DialogContent className="sm:max-w-[425px] md:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex gap-2 items-center">
            <i className="ti ti-user-plus" />
            <span>{t('invite_member_modal_title')}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          <MultiEmailInput
            handleAddClick={handleAddClick}
            handleRemoveClick={handleRemoveClick}
            error={emailError}
          />
          <div className="form-field">
            <div className="flex items-center justify-between">
              <label
                htmlFor="role"
                className="text-sm text-gray-500 font-normal"
              >
                {t('invite_member_modal_role_label')}
              </label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <i className="ti ti-info-circle" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{toolTipText}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={t('invite_member_modal_role_label')}
                />
                <i className="ti ti-chevron-down h-4 w-4 opacity-50" />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="form-field">
            <label
              htmlFor="workspace"
              className="text-sm text-gray-500 font-normal"
            >
              {t('invite_member_modal_workspaces_label')}
            </label>
            <ClientsDropdown
              selected={clientIds}
              onChange={handleClientsChange}
            />
          </div>
          {validationError && (
            <p className="text-red-500 text-sm">{validationError}</p>
          )}
        </div>

        <DialogFooter>
          <Button intent="secondary" onClick={closeModal}>
            {t('invite_member_modal_buttons_cancel')}
          </Button>
          <Button
            type="submit"
            disabled={members.length === 0 || isLoading}
            onClick={handleApplyClick}
          >
            {isLoading
              ? t('invite_member_modal_buttons_inviting', 'Inviting...')
              : t('invite_member_modal_buttons_invite', 'Send Invite')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewMemberModal;
